// 本地修改：客户要求留客弹窗只在电脑端出现。
// 手机端本来就不会触发（触屏没有「鼠标移出视口顶部」这个动作），
// 但某些浏览器用手指点击时会合成出 mouseout 事件，所以这里显式挡掉。
function glIsDesktopViewport() {
  const cfg = window.theme && window.theme.config;
  if (cfg && typeof cfg.mqlDesktop === 'boolean') return cfg.mqlDesktop;
  return window.matchMedia('(min-width: 1025px)').matches;
}

class BeforeYouLeave extends HTMLElement {
  constructor() {
    super();
    this.popup = this.querySelector('.modal--popup');
    // 本地修改：原来是 popup_expire_minutes（空闲多久后弹），
    // 现在改成「关闭后多少小时内不再弹」，默认 24 小时。
    this.expiresHours = Number(this.dataset.expireHours) || 24;
    // 遮罩层是 <before-you-leave> 的兄弟节点（在 .modal--popup 里、本元素之外），
    // 用 this.querySelector 取不到，之前这个监听器其实一直没绑上。
    this.overlay = this.parentElement?.querySelector('[id^="Modal-Overlay-"]');
    this.content = this.querySelector('.popup__inner');

    // 见过一次就写 localStorage（带到期时间戳）；同一标签页会话内也只弹一次。
    // 键名带 storageKey 命名空间：主题里另有一个订阅弹窗也写 localStorage，
    // 各用各的键，将来换主题/重置配置也不会互相撞。
    this.storageNs = this.dataset.storageKey || 'default';
    this.storageKey = `_gl_before_you_leave_dismiss_${this.storageNs}`;
    this.sessionKey = `_gl_before_you_leave_shown_${this.storageNs}`;
    this._storage = this.getBestStorage();

    this.querySelector('.popup__close')?.addEventListener('click', () => this.setClosePopup());
    this.overlay?.addEventListener('click', () => this.setClosePopup());

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', e => {
        if (e.target.matches('.section-before-you-leave')) this.setOpenPopup();
      });
      document.addEventListener('shopify:section:deselect', e => {
        if (e.target.matches('.section-before-you-leave')) this.setClosePopup();
      });
    }
  }

  connectedCallback() {
    if (!this.popup) return;
    if (Shopify.designMode) return;
    if (!glIsDesktopViewport()) return;

    // 见过一次、且还没到期 → 这个浏览器里彻底不挂监听，一次都不弹
    if (this.getStore(this.storageKey)) return;

    // 极少数情况：localStorage 被浏览器拦了（无痕 / 禁用 Cookie），
    // 退回「同一次浏览只弹一次」
    try {
      if (sessionStorage.getItem(this.sessionKey) === '1') return;
    } catch (_) {}

    // 本地修改：原逻辑是「空闲计时器」——用户停止操作一段时间就弹，
    // 体验很打扰。改为标准的「离站意图」(exit intent)：
    // 鼠标移出到视口上方（通常是去点关闭 / 地址栏 / 后退）时才弹。
    this.setExitIntentListener(true);
  }

  // 开关离站意图监听。关掉弹窗后必须摘掉监听，否则鼠标再往上移一次就又弹一次。
  setExitIntentListener(on) {
    if (!this.boundExitIntent) this.boundExitIntent = this.checkExitIntent.bind(this);
    document.removeEventListener('mouseout', this.boundExitIntent);
    if (on) document.addEventListener('mouseout', this.boundExitIntent);
  }

  disconnectedCallback() {
    if (Shopify.designMode) return;
    this.setExitIntentListener(false);
  }

  checkExitIntent(e) {
    // relatedTarget 为空且指针到达视口顶部 → 判断为要离开
    if (e.relatedTarget || e.toElement) return;
    if (e.clientY > 0) return;
    if (document.body.classList.contains('overflow-hidden')) return;
    this.setOpenPopup();
  }

  setOpenPopup() {
    if (!this.popup) return;
    // 已经开着就别再开一次（鼠标反复移到顶会重复触发，重播动画会闪）
    if (this.popup.hasAttribute('open')) return;

    // 弹过就记账：用户看完不点关闭、直接点页面里的链接走人，
    // 之前这里不写记录 → 新页面又从头弹一次，表现就是「一直弹」。
    this.setDismissed();

    try { sessionStorage.setItem(this.sessionKey, '1'); } catch (_) {}
    this.popup.setAttribute('open', 'true');
    this.animateOpen();
    document.body.classList.add('overflow-hidden');
    document.documentElement.setAttribute('scroll-lock', '');
  }

  setClosePopup() {
    if (!this.popup) return;
    // 关键修复：关掉就把离站意图监听摘掉。
    // 旧逻辑只 removeAttribute('open')，监听器还挂在 document 上，
    // 于是同一个页面里鼠标再往上移一次，弹窗又弹一次。
    this.setExitIntentListener(false);
    this.setDismissed();
    this.animateClose();
    setTimeout(() => {
      this.popup.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      document.documentElement.removeAttribute('scroll-lock');
    }, 100);
  }

  // 写入「到期时间戳」，之后 N 小时内不再弹
  setDismissed() {
    try {
      if (!this._storage) return;
      const until = Date.now() + this.expiresHours * 60 * 60 * 1000;
      this._storage.setItem(this.storageKey, JSON.stringify({ v: '1', exp: until }));
    } catch (_) {}
  }

  getStore(name) {
    try {
      if (!this._storage) return null;
      const raw = this._storage.getItem(name);
      if (!raw) return null;
      const payload = JSON.parse(raw);
      if (!payload || typeof payload.exp !== 'number') return null;
      if (Date.now() > payload.exp) {
        this._storage.removeItem(name);
        return null;
      }
      return payload.v;
    } catch (_) { return null; }
  }

  getBestStorage() {
    try {
      const k = '__byl_test__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return window.localStorage;
    } catch (_) {}
    try {
      const k2 = '__byl_test__';
      window.sessionStorage.setItem(k2, '1');
      window.sessionStorage.removeItem(k2);
      return window.sessionStorage;
    } catch (_) {}
    return null;
  }

  animateOpen() {
    Motion.timeline([
      [this.content, { opacity: [0, 1], transform: ['translate(-50%, -40%)', 'translate(-50%, -50%)'] }, { duration: 0.3, at: '-0.15' }]
    ]).finished;
  }

  animateClose() {
    Motion.timeline([
      [this.content, { opacity: [1, 0], transform: ['translate(-50%, -50%)', 'translate(-50%, -40%)'] }, { duration: 0.2 }]
    ]).finished;
  }
}
if (!customElements.get('before-you-leave')) customElements.define('before-you-leave', BeforeYouLeave);

class CopyCode extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.tooltip = this.querySelector('.tooltip');
    this.resetTooltipTimer = null;
    this.input = this.querySelector('input');
    this.resetInputTimer = null;
  }
  
  connectedCallback() {
    if (this.button) {
      this.button.addEventListener('click', this.copyCode.bind(this));
    }
  }

  copyCode() {
    console.log('copyCode');
    const text = this.getAttribute('data-text');
    if (!text) return;

    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }

    this.showSuccess();
  }

  showSuccess() {
    const defaultMessage = window.theme?.strings?.copy || 'Copy';
    const defaultInputValue = this.input?.value ?? '';

    if (this.tooltip && this.tooltip.dataset.success_message) {
      this.tooltip.textContent = this.tooltip.dataset.success_message;
      clearTimeout(this.resetTooltipTimer);
      this.resetTooltipTimer = setTimeout(() => {
        this.tooltip.textContent = defaultMessage;
      }, 1500);
    }

    if (this.input) {
      this.input.value = 'Copied!';
      clearTimeout(this.resetInputTimer);
      this.resetInputTimer = setTimeout(() => {
        this.input.value = defaultInputValue;
      }, 1500);
    }

    this.classList.add('copied');
    setTimeout(() => {
      this.classList.remove('copied');
    }, 1500);
  }
}
if (!customElements.get('copy-code')) customElements.define('copy-code', CopyCode);